import React, { useState, useEffect } from 'react';

interface AppConfigNumberInputProps {
  label: string;
  value: number;
  onChange: (value: string) => void;
  isPercentage?: boolean;
  isCurrency?: boolean;
  disabled?: boolean;
}

export const AppConfigNumberInput: React.FC<AppConfigNumberInputProps> = ({ 
  label, 
  value, 
  onChange, 
  isPercentage, 
  isCurrency, 
  disabled 
}) => {
  const getInitialStringValue = (num: number) => {
    if (isPercentage) {
      // Use toFixed to avoid floating point issues like 0.113 * 100 = 11.299999999999999
      return String(Number((num * 100).toFixed(4)));
    }
    return String(num);
  };

  const [localValue, setLocalValue] = useState(getInitialStringValue(value));

  // Sync with external value changes
  useEffect(() => {
    const numericLocal = isPercentage ? parseFloat(localValue) / 100 : parseFloat(localValue);
    
    // Only update if the numeric value is significantly different to avoid cursor jumps
    // We use a small epsilon for percentage comparisons
    const diff = Math.abs(numericLocal - value);
    const threshold = isPercentage ? 0.000001 : 0.1;

    if (isNaN(numericLocal) || diff > threshold) {
      setLocalValue(getInitialStringValue(value));
    }
  }, [value, isPercentage]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let val = e.target.value;
    
    // For currency, we strip everything except digits if we don't want decimals
    // Or we allow dots if we want decimals. In Colombia VIS threshold usually doesn't have decimals.
    if (isCurrency) {
      val = val.replace(/[^0-9]/g, '');
    } else {
      // Allow only digits and one dot for percentages
      val = val.replace(/[^0-9.]/g, '');
      const parts = val.split('.');
      if (parts.length > 2) {
        val = parts[0] + '.' + parts.slice(1).join('');
      }
    }

    setLocalValue(val);

    if (val !== '' && val !== '.') {
      let numericValue = parseFloat(val);
      if (isPercentage) numericValue = numericValue / 100;
      onChange(String(numericValue));
    } else if (val === '') {
      onChange('0');
    }
  };

  const formattedPreview = isCurrency ? new Intl.NumberFormat('es-CO', { 
    style: 'currency', 
    currency: 'COP', 
    maximumFractionDigits: 0 
  }).format(parseFloat(localValue) || 0) : null;

  return (
    <div className="w-full">
      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">{label}</label>
      <div className="relative mt-1">
        {isCurrency && (
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <span className="text-gray-400 sm:text-sm">$</span>
          </div>
        )}
        <input
          type="text"
          value={localValue}
          onChange={handleInputChange}
          className={`block w-full ${isCurrency ? 'pl-7' : 'px-3'} py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm ${disabled ? 'bg-gray-100' : 'bg-white'}`}
          disabled={disabled}
        />
        {isPercentage && (
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
            <span className="text-gray-400 sm:text-sm">%</span>
          </div>
        )}
      </div>
      {isCurrency && (
        <p className="mt-1 text-[10px] text-gray-400 font-medium italic">
          Vista previa: {formattedPreview}
        </p>
      )}
    </div>
  );
};
